import {
  AddressTypeEnum,
  EntityTypeEnum,
  Workspace,
} from '@sr-sdks/api-sdk-axios';
import { WORKSPACE_CONTACT_FIELDS } from '../../../components/WorkspaceDetails/ContactDetails/contactDetailsConfig';
import { normalizeCountryToIso2 } from './normalizeCountry';
import { WorkspaceDetailsFormValues } from './types';

/** Yes/No options for the represents-clients radio. */
export const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

/** Entity-type select options (labels mirror the legacy read-only view). */
export const ENTITY_TYPE_OPTIONS = [
  { label: 'Individual', value: EntityTypeEnum.Individual },
  { label: 'Company', value: EntityTypeEnum.Company },
  { label: 'Partnership', value: EntityTypeEnum.Partnership },
  {
    label: 'Self-employed/Sole Trader',
    value: EntityTypeEnum.SelfEmployedSoleTrader,
  },
  { label: 'Trust', value: EntityTypeEnum.Trust },
  { label: 'Other', value: EntityTypeEnum.Other },
];

/**
 * Compute the initial `canProceed` gate from the persisted workspace.
 *
 * Mirrors the legacy effect (WorkspaceDetailsForm.tsx ~251-272): individuals
 * and non-AU entities can always proceed; AU entities can proceed once they
 * have a (verified or unverified) business number, otherwise the gate blocks.
 */
const initialCanProceed = (workspace: Workspace): boolean => {
  if (workspace.entityType === EntityTypeEnum.Individual) return true;
  const country = (workspace.businessNumberCountry ?? '').toUpperCase();

  if (workspace.businessNumberVerified || country !== 'AU') return true;

  return Boolean(workspace.businessNumber);
};

/**
 * The single street address this form edits (v2 parity: saves emit
 * addresses: [ { addressType: 'street', ... }, ...preserved postal ]).
 * Deliberately NO fallback to other entries: prefilling from a postal
 * address would let a save re-emit a PO Box as the street address.
 */
export const workspaceStreetAddress = (workspace: Workspace) =>
  workspace.addresses?.find(
    (address) => address.addressType === AddressTypeEnum.Street,
  );

/**
 * Map a persisted workspace onto the workspace-details form values.
 * `isOnboarding` only changes the address-country fallback (see below).
 */
export const workspaceToEntityFormDefaults = (
  workspace: Workspace,
  { isOnboarding = false }: { isOnboarding?: boolean } = {},
): WorkspaceDetailsFormValues => {
  const street = workspaceStreetAddress(workspace);

  return {
    abnStatus: workspace.abnStatus ?? null,
    addressCity: street?.city ?? '',
    /**
     * Onboarding falls back to AU when the workspace has no street address
     * yet — legacy parity with the old form's `country: 'AU'` default. It also
     * arms the all-or-nothing address group so the address becomes required,
     * which onboarding demands and settings must not.
     *
     * Branched on whether a country was *stored*, not on whether the normaliser
     * could read it: `normalizeCountryToIso2` returns `''` deliberately so an
     * unreadable value surfaces as a required-field error, and defaulting on
     * that would quietly relabel a stored overseas address as Australian.
     */
    addressCountry: street?.country
      ? normalizeCountryToIso2(street.country)
      : isOnboarding
        ? 'AU'
        : '',
    addressLine1: street?.line1 ?? '',
    addressPostcode: street?.postcode ?? '',
    addressState: street?.state ?? '',
    businessNumber: workspace.businessNumber ?? '',
    /**
     * No AU fallback: legacy `workspaceToForm` maps
     * `businessNumberCountry: workspace.businessNumberCountry` with no default
     * (packages/frontend/src/schema/workspaceSchema.ts:204), so an empty
     * workspace opens onboarding with the country unset. Defaulting it to AU
     * pre-selected a country the user never chose, which also hid the `Other`
     * entity type (non-AU only) — APP-2237 QA notes 1 and 3.
     */
    businessNumberCountry: workspace.businessNumberCountry ?? '',
    businessNumberVerified: workspace.businessNumberVerified ?? false,
    canProceed: initialCanProceed(workspace),
    categories: workspace.categories ?? [],
    // Derived from WORKSPACE_CONTACT_FIELDS — one source of truth for the
    // contact role keys (config, schema, and defaults stay in lockstep).
    contacts: Object.fromEntries(
      WORKSPACE_CONTACT_FIELDS.map((field) => [
        field.name,
        workspace.contacts?.[field.name] ?? '',
      ]),
    ),
    entityName: workspace.entityName ?? '',
    entityType: workspace.entityType ?? '',
    gstStatus: workspace.gstStatus ?? null,
    phone: workspace.phone ?? '',
    representsClients:
      workspace.representsClients === true
        ? 'yes'
        : workspace.representsClients === false
          ? 'no'
          : '',
    taxResidency: workspace.taxResidency ?? '',
    website: workspace.website ?? '',
    workspaceName: workspace.workspaceName ?? '',
    workspaceType: workspace.workspaceType ?? undefined,
  };
};
